import { useEffect } from 'react';
import { Modal, Form as AntForm, Input, InputNumber, Select, message } from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authFetch } from '../../utils/api';
import { FormField, FormActions } from '../../shared/form';
import { TierScheduleSchema, type TierScheduleDTO } from '../../schemas/tier';

export interface ScheduleRecordForForm {
  id: number;
  dayOfWeek: number | null;
  startTime: string;
  endTime: string;
  downloadPercent: number;
  uploadPercent: number;
  priority: number;
}

interface Props {
  open: boolean;
  /** 排程的擁有者 tier id（用於 API 路徑） */
  sourceTierId: number;
  /**
   * 去品牌化 M4：排程隸屬的 tier row 品牌。排程寫入是既有 tier row 的寫入，
   * 目標品牌 = 該 tier 的品牌（列表已知），顯式帶 X-Target-Brand，絕不繼承檢視範圍。
   */
  sourceBrand: string;
  /** null = 新增、record = 編輯 */
  editing: ScheduleRecordForForm | null;
  onClose: () => void;
  onSaved: () => void;
}

const DAY_OPTIONS = [
  { label: '每天', value: null },
  { label: '星期一', value: 1 },
  { label: '星期二', value: 2 },
  { label: '星期三', value: 3 },
  { label: '星期四', value: 4 },
  { label: '星期五', value: 5 },
  { label: '星期六', value: 6 },
  { label: '星期日', value: 0 },
];

const DEFAULTS: TierScheduleDTO = {
  dayOfWeek: null,
  startTime: '00:00',
  endTime: '23:59',
  downloadPercent: 100,
  uploadPercent: 100,
  priority: 0,
};

function toInputTime(value: string | null | undefined): string {
  if (!value) return '00:00';
  return value.slice(0, 5);
}

function toApiTime(value: string): string {
  return value.length === 5 ? `${value}:00` : value;
}

export const ScheduleFormModal: React.FC<Props> = ({
  open,
  sourceTierId,
  sourceBrand,
  editing,
  onClose,
  onSaved,
}) => {
  const form = useForm<TierScheduleDTO>({
    resolver: zodResolver(TierScheduleSchema),
    mode: 'onBlur',
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      if (editing) {
        form.reset({
          dayOfWeek: editing.dayOfWeek ?? null,
          startTime: toInputTime(editing.startTime),
          endTime: toInputTime(editing.endTime),
          downloadPercent: editing.downloadPercent ?? 100,
          uploadPercent: editing.uploadPercent ?? 100,
          priority: editing.priority,
        });
      } else {
        form.reset(DEFAULTS);
      }
    }
  }, [open, editing, form]);

  const onSubmit: SubmitHandler<TierScheduleDTO> = async (values) => {
    const url = editing
      ? `/api/admin/tiers/${sourceTierId}/schedules/${editing.id}`
      : `/api/admin/tiers/${sourceTierId}/schedules`;
    const method = editing ? 'PUT' : 'POST';
    // M4：排程寫入是既有 tier row 的寫入，目標品牌 = 該 tier 的品牌，顯式帶
    // X-Target-Brand + body.targetBrand，絕不繼承檢視範圍 / activeBrand。
    const res = await authFetch(url, {
      method,
      headers: { 'X-Target-Brand': sourceBrand },
      body: JSON.stringify({
        ...values,
        startTime: toApiTime(values.startTime),
        endTime: toApiTime(values.endTime),
        sourceTierId,
        targetBrand: sourceBrand,
      }),
    });
    const result = await res.json();
    if (result.code === 0) {
      message.success(editing ? '排程已更新' : '排程新增成功');
      onSaved();
      onClose();
    } else {
      message.error(result.msg || '操作失敗');
    }
  };

  return (
    <Modal
      title={editing ? `編輯限速排程 #${editing.id}` : '新增限速排程'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
    >
      <FormProvider {...form}>
        <AntForm layout="vertical" onFinish={form.handleSubmit(onSubmit)}>
          <FormField<TierScheduleDTO>
            name="dayOfWeek"
            label="套用日期"
            help="留在「每天」代表每日都套用"
            control={<Select options={DAY_OPTIONS} placeholder="每天" />}
          />
          <FormField<TierScheduleDTO>
            name="startTime"
            label="開始時間"
            required
            control={<Input type="time" step={60} />}
          />
          <FormField<TierScheduleDTO>
            name="endTime"
            label="結束時間"
            required
            control={<Input type="time" step={60} />}
          />
          <FormField<TierScheduleDTO>
            name="downloadPercent"
            label="下載速度比例 (%)"
            required
            help="100 代表不降速，50 代表降為一半"
            control={<InputNumber min={0} max={100} style={{ width: '100%' }} />}
          />
          <FormField<TierScheduleDTO>
            name="uploadPercent"
            label="上傳速度比例 (%)"
            required
            help="100 代表不降速，50 代表降為一半"
            control={<InputNumber min={0} max={100} style={{ width: '100%' }} />}
          />
          <FormField<TierScheduleDTO>
            name="priority"
            label="優先序"
            required
            help="同時段多個排程時，數值越大越優先"
            control={<InputNumber min={0} style={{ width: '100%' }} />}
          />
          <FormActions
            onCancel={onClose}
            submitting={form.formState.isSubmitting}
            dirty={form.formState.isDirty}
            submitLabel={editing ? '儲存變更' : '建立排程'}
          />
        </AntForm>
      </FormProvider>
    </Modal>
  );
};
